import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Shirt, Info } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU, formatError, computeDegreasingPrice } from "@/components/shared/helpers";

const TYPE_LABELS = {
  degreasing: "Dégraissage",
  laundry_full: "Blanchisserie complète",
  laundry_punctual: "Demande ponctuelle",
};

/**
 * Onglet propriétaire "Dégraissage/Blanchisserie".
 * - Formulaire pour créer une demande (3 types).
 * - Historique des demandes avec statut + pastille de notification.
 */
export default function OwnerLaundry({ properties = [] }) {
  const [pricing, setPricing] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedProp, setSelectedProp] = useState(properties[0]?.id || "");
  const [degStart, setDegStart] = useState("");
  const [degEnd, setDegEnd] = useState("");
  const [laundryMode, setLaundryMode] = useState("laundry_full"); // laundry_full | laundry_punctual
  const [fullStart, setFullStart] = useState("");
  const [fullEnd, setFullEnd] = useState("");
  const [selectedArticles, setSelectedArticles] = useState({}); // {article_id: qty}
  const [punctualDesc, setPunctualDesc] = useState("");
  const [saving, setSaving] = useState(null); // "degreasing" | "laundry_full" | "laundry_punctual"

  // AUTO-remplissage date fin = début + 5 jours (délai min requis)
  const addDays = (iso, days) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const onDegStartChange = (val) => {
    setDegStart(val);
    if (val && !degEnd) setDegEnd(addDays(val, 5));
  };
  const onFullStartChange = (val) => {
    setFullStart(val);
    if (val && !fullEnd) setFullEnd(addDays(val, 5));
  };

  useEffect(() => {
    if (!selectedProp && properties[0]?.id) setSelectedProp(properties[0].id);
  }, [properties, selectedProp]);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        api.get("/admin/laundry-pricing"),
        api.get("/owner/laundry-requests"),
      ]);
      setPricing(pRes.data || []);
      setRequests(rRes.data || []);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const groupedPricing = useMemo(() => {
    return pricing.reduce((acc, a) => { (acc[a.category] ||= []).push(a); return acc; }, {});
  }, [pricing]);

  // Prix de dégraissage estimé pour le logement sélectionné (avec fallback métier)
  const degreasingQuote = useMemo(() => {
    const prop = properties.find((p) => p.id === selectedProp);
    return computeDegreasingPrice(prop);
  }, [properties, selectedProp]);

  const submit = async (request_type) => {
    if (!selectedProp) { toast({ title: "Sélectionnez un logement", variant: "destructive" }); return; }
    setSaving(request_type);
    try {
      const payload = { property_id: selectedProp, request_type };
      if (request_type === "degreasing") {
        payload.period_start = degStart; payload.period_end = degEnd;
      } else if (request_type === "laundry_full") {
        payload.period_start = fullStart; payload.period_end = fullEnd;
        payload.articles = Object.entries(selectedArticles)
          .filter(([_, qty]) => Number(qty) > 0)
          .map(([article_id, qty]) => {
            const art = pricing.find((a) => a.id === article_id);
            return { article_id, name: art?.name || "", qty: Number(qty), unit_price_ttc: Number(art?.price_ttc || 0) };
          });
      } else {
        payload.punctual_description = punctualDesc.trim();
      }
      await api.post("/owner/laundry-requests", payload);
      toast({ title: "Demande envoyée", description: "Vous recevrez la réponse ici même." });
      // Reset
      if (request_type === "degreasing") { setDegStart(""); setDegEnd(""); }
      else if (request_type === "laundry_full") { setFullStart(""); setFullEnd(""); setSelectedArticles({}); }
      else { setPunctualDesc(""); }
      await load();
    } catch (e) {
      toast({ title: "Demande refusée", description: formatError(e), variant: "destructive" });
    } finally { setSaving(null); }
  };

  const unreadCount = requests.filter((r) => !r.read_by_owner).length;

  const markRead = async (id) => {
    try {
      await api.patch(`/owner/laundry-requests/${id}/read`);
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, read_by_owner: true } : r)));
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="owner-laundry-tab" className="space-y-4">
      {/* Sélection logement si plusieurs */}
      {properties.length > 1 && (
        <Card data-testid="owner-laundry-property-select-card" className="border-white/10 bg-white/[0.08]">
          <CardContent className="pt-6">
            <Label className="text-xs text-slate-300">Logement concerné</Label>
            <Select value={selectedProp} onValueChange={setSelectedProp}>
              <SelectTrigger data-testid="owner-laundry-property-select" className="border-white/10 bg-white/[0.06]"><SelectValue placeholder="Choisir un logement" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.short_name || p.residence_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Section Dégraissage */}
      <Card data-testid="owner-laundry-degreasing-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader><CardTitle className="text-slate-100 flex items-center gap-2"><Shirt className="h-4 w-4" /> Dégraissage</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-100">
            Disponible uniquement en intersaison : du 30/04 au 15/06 ou du 20/09 au 05/12. Merci de nous prévenir au moins 5 jours à l&apos;avance.
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs text-slate-300">Début</Label><Input data-testid="owner-laundry-degreasing-start" type="date" value={degStart} onChange={(e) => onDegStartChange(e.target.value)} className="border-white/10 bg-white/[0.06]" /></div>
            <div><Label className="text-xs text-slate-300">Fin</Label><Input data-testid="owner-laundry-degreasing-end" type="date" value={degEnd} onChange={(e) => setDegEnd(e.target.value)} className="border-white/10 bg-white/[0.06]" /></div>
          </div>

          {/* Prix indicatif pour ce logement — configuré, estimé (fallback) ou à confirmer */}
          <div data-testid="owner-laundry-degreasing-price-block" className="rounded-md border border-white/10 bg-white/[0.03] p-2 text-xs">
            {degreasingQuote.source === "configured" && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-300">Tarif pour ce logement :</span>
                <span data-testid="owner-laundry-degreasing-price-value" className="text-slate-100 font-medium">
                  {degreasingQuote.price.toFixed(2)}€
                </span>
              </div>
            )}
            {degreasingQuote.source === "estimated" && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-300">Tarif estimé pour ce logement :</span>
                  <span data-testid="owner-laundry-degreasing-price-value" className="text-slate-100 font-medium">
                    {degreasingQuote.price.toFixed(2)}€
                  </span>
                </div>
                <div data-testid="owner-laundry-degreasing-price-warning" className="mt-1 rounded border border-amber-500/40 bg-amber-500/10 p-1.5 text-amber-100">
                  ⚠️ Tarif non configuré sur ce logement — prix estimé, à confirmer.
                </div>
              </>
            )}
            {degreasingQuote.source === "none" && (
              <div data-testid="owner-laundry-degreasing-price-tbd" className="rounded border border-amber-500/40 bg-amber-500/10 p-1.5 text-amber-100">
                Tarif à confirmer par notre équipe.
              </div>
            )}
          </div>

          <Button data-testid="owner-laundry-degreasing-submit" className="rounded-full bg-violet-500 hover:bg-violet-400" disabled={!!saving || !degStart || !degEnd} onClick={() => submit("degreasing")}>
            {saving === "degreasing" ? "Envoi…" : "Envoyer la demande"}
          </Button>
        </CardContent>
      </Card>

      {/* Section Blanchisserie */}
      <Card data-testid="owner-laundry-full-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader><CardTitle className="text-slate-100 flex items-center gap-2"><Shirt className="h-4 w-4" /> Blanchisserie</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input data-testid="owner-laundry-mode-full" type="radio" checked={laundryMode === "laundry_full"} onChange={() => setLaundryMode("laundry_full")} />
              Blanchisserie complète du logement
            </label>
            <label className="flex items-center gap-2">
              <input data-testid="owner-laundry-mode-punctual" type="radio" checked={laundryMode === "laundry_punctual"} onChange={() => setLaundryMode("laundry_punctual")} />
              Demande ponctuelle
            </label>
          </div>

          {laundryMode === "laundry_full" ? (
            <>
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-100">
                Disponible uniquement en intersaison + préavis 5 jours minimum.
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="text-xs text-slate-300">Début</Label><Input data-testid="owner-laundry-full-start" type="date" value={fullStart} onChange={(e) => onFullStartChange(e.target.value)} className="border-white/10 bg-white/[0.06]" /></div>
                <div><Label className="text-xs text-slate-300">Fin</Label><Input data-testid="owner-laundry-full-end" type="date" value={fullEnd} onChange={(e) => setFullEnd(e.target.value)} className="border-white/10 bg-white/[0.06]" /></div>
              </div>
              {Object.keys(groupedPricing).sort().map((cat) => (
                <div key={cat} data-testid={`owner-laundry-articles-${cat}`} className="rounded-md border border-white/10 p-2">
                  <div className="text-xs font-medium text-slate-200 mb-2">{cat === "blanchisserie" ? "Blanchisserie" : "Ameublement"}</div>
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {groupedPricing[cat].map((a) => (
                      <div key={a.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-200 flex-1 truncate">{a.name} <span className="text-slate-500">({Number(a.price_ttc).toFixed(2)}€)</span></span>
                        <Input
                          data-testid={`owner-laundry-article-${a.id}-qty`}
                          type="number" min="0"
                          value={selectedArticles[a.id] || ""}
                          onChange={(e) => setSelectedArticles((prev) => ({ ...prev, [a.id]: e.target.value }))}
                          placeholder="0"
                          className="h-8 w-16 border-white/10 bg-white/[0.06] text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Button data-testid="owner-laundry-full-submit" className="rounded-full bg-violet-500 hover:bg-violet-400" disabled={!!saving || !fullStart || !fullEnd || Object.values(selectedArticles).every((q) => !Number(q))} onClick={() => submit("laundry_full")}>
                {saving === "laundry_full" ? "Envoi…" : "Envoyer la demande"}
              </Button>
            </>
          ) : (
            <>
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <span>Toute l&apos;année. Décrivez précisément votre besoin.</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="owner-laundry-punctual-pricing-info"
                      type="button"
                      className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-200 underline underline-offset-2"
                      aria-label="Voir la grille tarifaire"
                    >
                      <Info className="h-3.5 w-3.5" /> Grille tarifaire
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    data-testid="owner-laundry-punctual-pricing-popover"
                    align="start"
                    side="bottom"
                    sideOffset={6}
                    collisionPadding={16}
                    className="w-[380px] border-white/10 bg-[#0f1320] text-slate-100 p-0 text-xs"
                  >
                    <div className="max-h-[min(60vh,420px)] overflow-y-auto p-3">
                      <div className="text-slate-300 mb-2">Grille tarifaire indicative (lecture seule)</div>
                      {Object.keys(groupedPricing).sort().map((cat) => (
                        <div key={cat} className="mb-3">
                          <div className="font-medium text-slate-200 mb-1 sticky top-0 bg-[#0f1320] pb-1">{cat === "blanchisserie" ? "Blanchisserie" : "Ameublement"}</div>
                          <ul className="space-y-0.5">
                            {groupedPricing[cat].map((a) => (
                              <li key={a.id} className="flex justify-between gap-3">
                                <span className="text-slate-300">{a.name}</span>
                                <span className="text-slate-100 font-medium">{Number(a.price_ttc).toFixed(2)}€</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Textarea data-testid="owner-laundry-punctual-desc" value={punctualDesc} onChange={(e) => setPunctualDesc(e.target.value)} className="border-white/10 bg-white/[0.06] min-h-[80px]" placeholder="Ex : nettoyage de 2 couettes King Size et 4 oreillers pour intervention urgente." />
              <Button data-testid="owner-laundry-punctual-submit" className="rounded-full bg-violet-500 hover:bg-violet-400" disabled={!!saving || !punctualDesc.trim()} onClick={() => submit("laundry_punctual")}>
                {saving === "laundry_punctual" ? "Envoi…" : "Envoyer la demande"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <p data-testid="owner-laundry-info" className="text-xs text-slate-400 px-1">
        Vous recevrez la réponse ici même, avec la date retenue par notre équipe — pas nécessairement par email.
      </p>

      {/* Historique des demandes */}
      <Card data-testid="owner-laundry-history" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            Mes demandes
            {unreadCount > 0 && <span data-testid="owner-laundry-unread-badge" className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{unreadCount}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-slate-400 text-sm">Chargement…</div>
            : requests.length === 0 ? <div className="text-slate-400 text-sm">Aucune demande pour l&apos;instant.</div>
            : (
              <div className="space-y-2">
                {requests.map((r) => {
                  const isUnread = !r.read_by_owner;
                  const tone = r.status === "confirmed" ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
                    : r.status === "rejected" ? "bg-slate-500/20 text-slate-200 border-slate-500/40"
                    : "bg-amber-500/20 text-amber-200 border-amber-500/40";
                  return (
                    <div
                      key={r.id}
                      data-testid={`owner-laundry-request-${r.id}`}
                      className={`rounded-md border p-3 ${isUnread ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-white/[0.03]"}`}
                      onClick={() => isUnread && markRead(r.id)}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <div className="text-sm text-slate-100 font-medium flex items-center gap-2">
                            {TYPE_LABELS[r.request_type]}
                            <Badge className={`${tone} border`}>{r.status === "confirmed" ? "Confirmée" : r.status === "rejected" ? "Refusée" : "En attente"}</Badge>
                            {isUnread && <span data-testid={`owner-laundry-request-${r.id}-dot`} className="h-2 w-2 rounded-full bg-red-500" />}
                          </div>
                          {r.period_start && r.period_end && (
                            <div className="text-xs text-slate-400">Période souhaitée : {formatDateEU(r.period_start)} → {formatDateEU(r.period_end)}</div>
                          )}
                          {r.status === "confirmed" && r.confirmed_date && (
                            <div data-testid={`owner-laundry-request-${r.id}-confirmed-date`} className="text-sm text-emerald-300 mt-1">
                              Intervention confirmée le <b>{formatDateEU(r.confirmed_date)}</b>
                              {r.final_price_ttc != null && <> — <b>{Number(r.final_price_ttc).toFixed(2)}€</b></>}
                            </div>
                          )}
                          {r.admin_note && <div className="text-xs text-slate-300 mt-1">Note : {r.admin_note}</div>}
                        </div>
                        <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
